import {
  ListItem,
  DcuplItemsOptions,
  Projection,
  SortingProjection,
  SortDirection,
  ScriptController,
} from '@dcupl/common';
import { difference, orderBy } from 'lodash-es';

import { DcuplModel, ModelParser } from '../../model-parser';

export class ItemService<ListType extends ListItem = ListItem> {
  constructor(
    public modelParser: ModelParser,
    private scriptController: ScriptController,
  ) {}

  public getItem(
    itemId: string,
    dataAsMap: Map<string, ListType>,
    model: DcuplModel<ListType>,
    options?: DcuplItemsOptions<ListType>,
  ): ListType | null {
    const projection: Projection<ListType> = options?.projection || { $: true };
    const dataEntry = dataAsMap.get(itemId);

    if (!dataEntry) {
      return null;
    }

    const objToReturn = this.getProjectedData(projection, model, dataEntry);

    return objToReturn;
  }

  public getManyItems(
    itemKeys: string[],
    dataAsMap: Map<string, ListType>,
    model: DcuplModel<ListType>,
    options?: DcuplItemsOptions<ListType>,
  ): ListType[] {
    const items: ListType[] = [];

    for (const key of itemKeys) {
      const projection: Projection<ListType> = options?.projection || { $: true };
      const dataEntry = dataAsMap.get(key);

      if (!dataEntry) {
        continue;
      }

      const objToReturn = this.getProjectedData(projection, model, dataEntry);

      items.push(objToReturn);
    }
    return items;
  }

  public getItems(
    dataAsMap: Map<string, ListType>,
    model: DcuplModel<ListType>,
    options?: DcuplItemsOptions<ListType>,
  ): ListType[] {
    const result = this.getItemsAsMap(dataAsMap, model, options);

    return Array.from(result.values());
  }

  public getItemsAsMap(
    dataAsMap: Map<string, ListType>,
    model?: DcuplModel<ListType>,
    options?: DcuplItemsOptions<ListType>,
  ): Map<string, ListType> {
    const start = options?.start || 0;
    const count = options?.count || dataAsMap.size;
    const projection: Projection<ListType> = options?.projection || { $: true };

    if (this.isValidSortProjection(options?.sort)) {
      dataAsMap = this.getSortedItems(dataAsMap, options, model);
    }

    if (this.isValidScriptSorting(options?.sort)) {
      dataAsMap = this.getSortedItemsBasedOnScript(dataAsMap, options);
    }

    const result = this.getPagedData(dataAsMap, start, count, projection, model);

    return result;
  }

  protected getSortedItemsBasedOnScript(
    data: Map<string, ListType>,
    options?: DcuplItemsOptions<ListType>,
  ): Map<string, ListType> {
    const dataAsArray = Array.from(data.values());
    const scriptName = options?.sort?.script?.key || '';
    const sortOptions = options?.sort?.script?.options || undefined;
    if (!this.scriptController.hasScript(scriptName)) {
      return data;
    }

    const sortedArray: ListType[] = this.scriptController.executeScript(scriptName, 'sort', {
      options: sortOptions,
      items: dataAsArray,
    });

    return new Map(
      sortedArray.map((object) => {
        return [object.key, object];
      }),
    );
  }
  protected getSortedItems(
    data: Map<string, ListType>,
    options?: DcuplItemsOptions<ListType>,
    model?: DcuplModel<ListType>,
  ): Map<string, ListType> {
    const attributes = [...options!.sort!.attributes];

    const dataAsArray = Array.from(data.values());

    const dataTransformer = (listItem: ListType, attr: string, undefinedValue: any): any => {
      const value = listItem[attr];
      if (typeof value === 'undefined' || value === null) {
        return undefinedValue;
      }
      if (typeof value === 'string') {
        return value.toLowerCase();
      }

      if (typeof value === 'object') {
        if (typeof value?.key === 'string') {
          return value.key.toLowerCase();
        }
      }

      return value;
    };

    const transformerFn = attributes.map((attribute: any, index: number) => (entry: any) => {
      const relevantOrder = options!.sort!.order.at(index) as SortDirection;

      const undefinedValue = this.getUndefinedValueForSorting(attribute, relevantOrder, model);
      return dataTransformer(entry, attribute, undefinedValue);
    });
    const order = [...options!.sort!.order].map((o) => o.toLowerCase());
    const sortedArray: ListType[] = orderBy(dataAsArray, [...transformerFn], order as any);

    return new Map(
      sortedArray.map((object) => {
        return [object.key, object];
      }),
    );
  }

  private getUndefinedValueForSorting(attribute: string, direction: SortDirection, model?: DcuplModel<ListType>): any {
    const property = model?.properties.get(attribute);

    if (property) {
      if (property.type === 'string') {
        if (direction == 'ASC') {
          return undefined;
        }
        if (direction == 'DESC') {
          return '';
        }
      }
      if (
        property.type === 'int' ||
        property.type === 'float' ||
        property.type === 'date' ||
        property.type === 'boolean'
      ) {
        if (direction == 'ASC') {
          return Infinity;
        }
        if (direction == 'DESC') {
          return -Infinity;
        }
      }
    }
  }

  public isValidProjection(projection?: Projection<ListType>): boolean {
    if (!projection) {
      return false;
    }

    if (projection.$ === true && Object.keys(projection).length === 1) {
      return false;
    }
    return true;
  }

  public getPagedData(
    data: Map<string, any>,
    start: number,
    count: number,
    projection?: Projection<ListType>,
    model?: DcuplModel<ListType>,
  ): Map<string, any> {
    const it = data.entries();
    const pagedData = new Map();

    // fast return - if we want to return the full default-projected dataset
    if (start === 0 && count === data.size && projection?.$ === true && Object.keys(projection).length === 1) {
      return data;
    }

    for (let i = 0; i < start + count; i++) {
      if (i < start) {
        it.next();
        continue;
      }
      if (i > start + count) {
        break;
      }

      const item = it.next().value;
      if (item) {
        if (model && projection && this.isValidProjection(projection)) {
          const objToReturn = this.getProjectedData(projection, model, item[1] as ListType);
          pagedData.set(item[0], objToReturn);
        } else {
          pagedData.set(item[0], item[1]);
        }
      }
    }

    return pagedData;
  }

  protected isValidScriptSorting(sort: SortingProjection<any> | undefined): boolean {
    if (!sort) {
      return false;
    }
    if (!sort.script) {
      return false;
    }
    return true;
  }

  protected isValidSortProjection(sort: SortingProjection<any> | undefined): boolean {
    const validOrderValues: SortDirection[] = ['ASC', 'DESC'];

    if (
      !sort ||
      !Array.isArray(sort.attributes) ||
      sort.attributes.length === 0 ||
      !Array.isArray(sort.order) ||
      sort.attributes.length !== sort.order.length
    ) {
      return false;
    }

    if (difference(sort.order, validOrderValues).length > 0) {
      return false;
    }

    return true;
  }

  public getProjectedData(
    projection: Projection<ListType>,
    model: DcuplModel<ListType>,
    dataEntry: ListType,
  ): ListType {
    let objToReturn = { key: dataEntry.key } as ListType;

    if (projection.$ === true) {
      objToReturn = Object.assign({}, dataEntry);
    }

    if (projection.$$ === true) {
      objToReturn = Object.assign({}, dataEntry);
      model.references.forEach((ref) => {
        if (!(projection as any)[ref.key]) {
          (projection as any)[ref.key] = { $: true };
        }
      });
    }

    const keys: Array<keyof ListType> = Object.keys(projection);

    keys
      .filter((key) => key !== '$' && key !== '$$')
      .forEach((key) => {
        const projectionValue = (projection as ListType)[key];
        if (projectionValue === false) {
          delete objToReturn[key];
          return;
        }

        if (projectionValue === true) {
          objToReturn[key] = dataEntry[key];
          return;
        }

        if (typeof projectionValue === 'object') {
          const reference = model.references.get(key as string);
          if (reference) {
            const remoteModel = this.modelParser.models.get(reference.model) as DcuplModel<any> | undefined;
            if (remoteModel) {
              if (reference.type === 'multiValued') {
                const value: ListType[] = dataEntry[key] || [];

                objToReturn[key] = value
                  .map((entryKey: ListType) => {
                    const entry = remoteModel.getEntry(entryKey.key);
                    if (entry) {
                      return this.getProjectedData(projectionValue, remoteModel, entry);
                    }

                    if (this.modelParser.initOptions.referenceMetadata?.enabled) {
                      return { key: entryKey.key, _dcupl_ref_: 'miss' };
                    } else {
                      return null;
                    }
                  })
                  .filter(Boolean) as ListType[keyof ListType];
              } else if (reference.type === 'singleValued') {
                const value: ListType = dataEntry[key];
                if (value) {
                  const entry = remoteModel?.getEntry(value.key);
                  if (entry) {
                    objToReturn[key] = this.getProjectedData(projectionValue, remoteModel, entry) as any;
                  } else {
                    if (this.modelParser.initOptions.referenceMetadata?.enabled) {
                      objToReturn[key] = { key: value.key, _dcupl_ref_: 'miss' } as any;
                    } else {
                      delete objToReturn[key];
                    }
                  }
                }
              }
            }
          }
        }
      });

    return objToReturn;
  }
}
